import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import EntityName, { InvalidEntityNameError } from "legal-entities/entity-names/entity-name";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

interface EntityNameInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  initialValue?: EntityName;
  label?: string;
  /**
   * This method is called when the name instance is modified. This callback is only
   * registered initially, to reregister the handler, change the key
   * @returns ({@link EntityName}, undefined) when successful
   * @returns (undefined, errorMessage: string) when unsuccessful
   */
  onChange?: (entityName?: EntityName, errorMessage?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export default function EntityNameInput(props: Readonly<EntityNameInputProps>) {
  const { idPrefix, className, variant, disabled, label, initialValue, onChange, onKeyDown, ...otherProps } = props;

  const [entityNameText, setEntityNameText] = useState<string>(initialValue?.value ?? "");
  const [entityNameError, setEntityNameError] = useState<string>("");

  const entityNameIsDirty = useRef<boolean>(false);

  const debouncedValidate = useMemo(() => debounce((entityName: string, isDirty: boolean) => {
    let newEntityName: EntityName | undefined;
    let errorMessage: string = "";

    if(!isDirty) return;
    try {
      newEntityName = new EntityName(entityName);
    } catch (error) {
      if (error instanceof InvalidEntityNameError) {
        errorMessage = error.message;
      } else {
        throw error;
      }
    } finally {
      setEntityNameError(errorMessage);
      onChange?.(newEntityName, errorMessage);
    }
  // We cannot guarantee onChange being stable, if it changes, change the key to recreate the component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, 300), []);

  useEffect(() => {
    debouncedValidate(entityNameText, entityNameIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [entityNameText, debouncedValidate]);

  return (
    <InputField
      id={idPrefix ? idPrefix + "-entity-name-input" : "entity-name-input"}
      className={className}
      label={label ?? "Entity Name"}
      onChange={(event) => {
        setEntityNameText(prev => {
          if(entityNameIsDirty.current) return event.target.value;
          if(prev !== event.target.value)
          {
            entityNameIsDirty.current = true;
            return event.target.value;
          }
          return prev;
        });
      }}
      required
      disabled={disabled}
      value={entityNameText}
      error={!!entityNameError}
      helperText={entityNameError}
      variant={variant ?? "outlined"}
      autoComplete="organization"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}
